{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://fortemi.dev/schemas/knowledge-shard.schema.json",
  "title": "Knowledge Shard (matric-shard) conformance schema",
  "description": "Transitional @fortemi/core validation copy for Knowledge Shards. The Fortemi server owns the cross-repository contract; server compatibility requires a pinned schema receipt, named profile, and producer/consumer conformance evidence.",
  "type": "object",
  "$defs": {
    "isoDateTime": {
      "type": "string",
      "description": "ISO 8601 timestamp",
      "minLength": 1
    },
    "jsonObject": {
      "type": "object",
      "additionalProperties": true
    },
    "nullableJsonObject": {
      "type": ["object", "null"],
      "additionalProperties": true
    },
    "stringArray": {
      "type": "array",
      "items": { "type": "string" }
    },
    "manifest": {
      "type": "object",
      "additionalProperties": false,
      "required": [
        "version",
        "matric_version",
        "format",
        "created_at",
        "components",
        "counts",
        "checksums",
        "min_reader_version"
      ],
      "properties": {
        "version": { "type": "string", "minLength": 1 },
        "matric_version": { "type": "string", "minLength": 1 },
        "format": { "const": "matric-shard" },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "components": {
          "type": "array",
          "items": {
            "enum": [
              "notes",
              "collections",
              "tags",
              "templates",
              "links",
              "embedding_sets",
              "embedding_configs",
              "embedding_set_members",
              "embeddings",
              "skos_schemes",
              "skos_concepts",
              "skos_relations",
              "note_skos_tags",
              "provenance_edges",
              "community_assignments",
              "communities",
              "graph_edges",
              "graph_sources"
            ]
          }
        },
        "counts": {
          "type": "object",
          "additionalProperties": { "type": "integer", "minimum": 0 }
        },
        "checksums": {
          "type": "object",
          "description": "filename -> sha256 hex",
          "additionalProperties": { "type": "string", "pattern": "^[0-9a-f]{64}$" }
        },
        "min_reader_version": { "type": "string", "minLength": 1 },
        "migrated_from": {
          "type": ["string", "null"],
          "minLength": 1
        },
        "migration_history": {
          "type": "array",
          "items": {
            "type": "object",
            "additionalProperties": false,
            "required": [
              "from_version",
              "to_version",
              "migrated_at",
              "migrated_by",
              "changes"
            ],
            "properties": {
              "from_version": { "type": "string", "minLength": 1 },
              "to_version": { "type": "string", "minLength": 1 },
              "migrated_at": { "$ref": "#/$defs/isoDateTime" },
              "migrated_by": { "type": "string", "minLength": 1 },
              "changes": { "$ref": "#/$defs/stringArray" }
            }
          }
        },
        "layout": { "type": "object" }
      }
    },
    "attachmentReference": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "path", "mime", "checksum", "bytes"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "path": { "type": "string", "minLength": 1 },
        "mime": { "type": ["string", "null"] },
        "checksum": { "type": "string", "minLength": 1 },
        "bytes": { "type": "integer", "minimum": 0 }
      }
    },
    "attachmentProjection": {
      "type": "object",
      "additionalProperties": false,
      "required": ["extracted_text", "attachment"],
      "properties": {
        "extracted_text": { "type": ["string", "null"] },
        "attachment": { "$ref": "#/$defs/attachmentReference" }
      }
    },
    "note": {
      "type": "object",
      "additionalProperties": false,
      "description": "One row of the shard `notes` component. Field names are the server shard contract, not the PGlite table column names.",
      "required": [
        "id",
        "title",
        "original_content",
        "revised_content",
        "format",
        "source",
        "starred",
        "archived",
        "tags",
        "created_at",
        "updated_at"
      ],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "title": { "type": ["string", "null"] },
        "original_content": { "type": "string" },
        "revised_content": { "type": ["string", "null"] },
        "collection_id": { "type": ["string", "null"] },
        "attachments": {
          "type": "array",
          "items": { "$ref": "#/$defs/attachmentProjection" }
        },
        "format": { "type": "string" },
        "source": { "type": "string" },
        "starred": { "type": "boolean" },
        "archived": { "type": "boolean" },
        "tags": { "type": "array", "items": { "type": "string" } },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "updated_at": { "$ref": "#/$defs/isoDateTime" },
        "deleted_at": { "type": ["string", "null"] }
      }
    },
    "collection": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "description", "parent_id", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "description": { "type": ["string", "null"] },
        "parent_id": { "type": ["string", "null"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "note_count": { "type": "integer", "minimum": 0 }
      }
    },
    "tag": {
      "type": "object",
      "additionalProperties": false,
      "required": ["name", "created_at"],
      "properties": {
        "name": { "type": "string", "minLength": 1 },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "template": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "description", "content", "format", "default_tags", "collection_id", "created_at", "updated_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "description": { "type": ["string", "null"] },
        "content": { "type": "string" },
        "format": { "type": "string" },
        "default_tags": { "$ref": "#/$defs/stringArray" },
        "collection_id": { "type": ["string", "null"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "updated_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "link": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "from_note_id", "to_note_id", "kind", "score", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "from_note_id": { "type": "string", "minLength": 1 },
        "to_note_id": { "type": ["string", "null"] },
        "to_url": { "type": ["string", "null"] },
        "kind": { "type": "string" },
        "score": { "type": ["number", "null"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "metadata": { "$ref": "#/$defs/nullableJsonObject" }
      }
    },
    "embeddingSet": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "model", "dimension"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "slug": { "type": ["string", "null"] },
        "description": { "type": ["string", "null"] },
        "purpose": { "type": ["string", "null"] },
        "document_count": { "type": "integer", "minimum": 0 },
        "embedding_count": { "type": "integer", "minimum": 0 },
        "is_system": { "type": "boolean" },
        "keywords": { "$ref": "#/$defs/stringArray" },
        "model": { "type": "string" },
        "dimension": { "type": "integer", "minimum": 1 },
        "kind": { "enum": ["physical", "filter", "virtual"] },
        "mode": { "type": ["string", "null"] },
        "truncate_dimension": { "type": ["integer", "null"], "minimum": 1 },
        "criteria": { "$ref": "#/$defs/nullableJsonObject" },
        "source": { "$ref": "#/$defs/nullableJsonObject" },
        "compatibility": { "$ref": "#/$defs/nullableJsonObject" },
        "materialization": { "$ref": "#/$defs/nullableJsonObject" },
        "freshness": { "$ref": "#/$defs/nullableJsonObject" },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "updated_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "embeddingSetMember": {
      "type": "object",
      "additionalProperties": false,
      "required": ["embedding_set_id", "note_id"],
      "properties": {
        "embedding_set_id": { "type": "string", "minLength": 1 },
        "note_id": { "type": "string", "minLength": 1 },
        "embedding_id": { "type": "string", "minLength": 1 },
        "membership_type": { "type": "string" },
        "added_at": { "$ref": "#/$defs/isoDateTime" },
        "added_by": { "type": ["string", "null"] }
      }
    },
    "embeddingConfig": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "description", "model", "dimension", "chunk_size", "chunk_overlap", "is_default"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "description": { "type": ["string", "null"] },
        "model": { "type": "string" },
        "dimension": { "type": "integer", "minimum": 1 },
        "chunk_size": { "type": "integer", "minimum": 1 },
        "chunk_overlap": { "type": "integer", "minimum": 0 },
        "is_default": { "type": "boolean" }
      }
    },
    "embedding": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "note_id", "vector"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "note_id": { "type": "string", "minLength": 1 },
        "chunk_index": { "type": "integer", "minimum": 0 },
        "text": { "type": "string" },
        "vector": { "type": "array", "items": { "type": "number" } },
        "model": { "type": "string" },
        "embedding_set_id": { "type": "string", "minLength": 1 },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "skosScheme": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "title", "description", "created_at", "updated_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "title": { "type": "string" },
        "description": { "type": ["string", "null"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "updated_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "skosConcept": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "scheme_id", "pref_label", "alt_labels", "definition", "created_at", "updated_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "scheme_id": { "type": "string", "minLength": 1 },
        "pref_label": { "type": "string" },
        "alt_labels": { "$ref": "#/$defs/stringArray" },
        "definition": { "type": ["string", "null"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" },
        "updated_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "skosRelation": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "source_concept_id", "target_concept_id", "relation_type", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "source_concept_id": { "type": "string", "minLength": 1 },
        "target_concept_id": { "type": "string", "minLength": 1 },
        "relation_type": { "enum": ["broader", "narrower", "related"] },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "noteSkosTag": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "note_id", "concept_id", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "note_id": { "type": "string", "minLength": 1 },
        "concept_id": { "type": "string", "minLength": 1 },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "provenanceEdge": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "entity_type", "entity_id", "activity", "agent", "started_at", "ended_at", "attributes"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "entity_type": { "type": "string" },
        "entity_id": { "type": "string", "minLength": 1 },
        "activity": { "type": "string" },
        "agent": { "type": "string" },
        "started_at": { "$ref": "#/$defs/isoDateTime" },
        "ended_at": { "type": ["string", "null"] },
        "attributes": { "$ref": "#/$defs/nullableJsonObject" }
      }
    },
    "artifactFreshness": {
      "type": "object",
      "additionalProperties": false,
      "required": ["status"],
      "properties": {
        "status": { "enum": ["fresh", "stale", "unknown"] },
        "checked_at": { "$ref": "#/$defs/isoDateTime" },
        "stale_reason": { "type": "string" },
        "source_hashes": { "$ref": "#/$defs/jsonObject" }
      }
    },
    "graphSource": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "name", "kind", "input_hash", "freshness", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "kind": { "enum": ["link", "similarity", "search", "manual", "imported"] },
        "source_table": { "type": ["string", "null"] },
        "embedding_set_id": { "type": ["string", "null"] },
        "virtual_set_id": { "type": ["string", "null"] },
        "model": { "type": ["string", "null"] },
        "dimension": { "type": ["integer", "null"], "minimum": 1 },
        "truncate_dimension": { "type": ["integer", "null"], "minimum": 1 },
        "metric": { "type": ["string", "null"] },
        "algorithm": { "type": ["string", "null"] },
        "parameters": { "$ref": "#/$defs/jsonObject" },
        "input_hash": { "type": "string" },
        "freshness": { "$ref": "#/$defs/artifactFreshness" },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "graphEdge": {
      "type": "object",
      "additionalProperties": false,
      "required": ["graph_source_id", "from_note_id", "to_note_id", "weight", "kind"],
      "properties": {
        "graph_source_id": { "type": "string", "minLength": 1 },
        "from_note_id": { "type": "string", "minLength": 1 },
        "to_note_id": { "type": "string", "minLength": 1 },
        "weight": { "type": "number" },
        "kind": { "enum": ["link", "similarity", "manual"] },
        "rank": { "type": ["integer", "null"], "minimum": 0 },
        "metadata": { "$ref": "#/$defs/jsonObject" }
      }
    },
    "community": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "label": { "type": ["string", "null"] },
        "rank": { "type": ["integer", "null"], "minimum": 0 },
        "size": { "type": ["integer", "null"], "minimum": 0 },
        "confidence": { "type": ["number", "null"] },
        "representative_note_ids": { "$ref": "#/$defs/stringArray" },
        "metadata": { "$ref": "#/$defs/jsonObject" }
      }
    },
    "communitySet": {
      "type": "object",
      "additionalProperties": false,
      "required": ["id", "graph_source_id", "name", "source_type", "input_hash", "freshness", "communities", "created_at"],
      "properties": {
        "id": { "type": "string", "minLength": 1 },
        "graph_source_id": { "type": "string", "minLength": 1 },
        "name": { "type": "string" },
        "source_type": { "enum": ["precomputed", "dynamic-snapshot", "user-authored", "imported"] },
        "algorithm": { "type": ["string", "null"] },
        "parameters": { "$ref": "#/$defs/jsonObject" },
        "input_hash": { "type": "string" },
        "freshness": { "$ref": "#/$defs/artifactFreshness" },
        "communities": {
          "type": "array",
          "items": { "$ref": "#/$defs/community" }
        },
        "created_at": { "$ref": "#/$defs/isoDateTime" }
      }
    },
    "communityAssignment": {
      "type": "object",
      "additionalProperties": false,
      "required": ["community_set_id", "community_id", "note_id", "source_type"],
      "properties": {
        "community_set_id": { "type": "string", "minLength": 1 },
        "community_id": { "type": "string", "minLength": 1 },
        "note_id": { "type": "string", "minLength": 1 },
        "confidence": { "type": ["number", "null"] },
        "source_type": { "enum": ["precomputed", "dynamic-snapshot", "user-authored", "imported"] },
        "metadata": { "$ref": "#/$defs/jsonObject" }
      }
    }
  }
}
